import { useEffect, useState } from 'react';
import { DateRange, type Range } from 'react-date-range';
import { Outlet } from 'react-router-dom';
import styled from 'styled-components';

import { HeaderPage } from 'components/header/HeaderPage';
import { CustomButton } from 'components/header/style';
import { Button, Row, Tooltip, Typography, formatDate, openModal } from 'enable-ui';

import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';
import type { Institution } from 'modules/institutions/core/type';
import { useUserProfileController } from 'modules/layout/core/constrollers/user-profile';

import { ButtonOnboardingResults, TooltipStyle } from '../styles/learner-layout';

import { ReactComponent as AddIcon } from 'assets/icons/add.svg';
import { ReactComponent as DropdownIcon } from 'assets/icons/down.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as ImportIcon } from 'assets/icons/import.svg';

import { endOfDay, startOfDay } from 'date-fns';
import { checkRole, downloadURI } from 'helpers';
import { useBoolean } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';

import { COLOR, COLOR_CONFIG } from 'constants/color';

import { useLearnLayout } from '../core/controller/leaner-layout';
import { ImportModal } from './ImportModal';

type Step = 'ListInstitution' | 'ExportSelection' | 'DateSelection' | '';

const defaultState: Range[] = [
  {
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  },
];

export const LearnerLayout = () => {
  const { goToAddPage } = useLearnLayout();
  const { userProfile } = useUserProfileController();
  const isAdmin = !userProfile?.institution;

  const institutionState = useFetchApiToState<any, any>({
    url: '/institutions',
    data: {
      page: 1,
      limit: 9999,
    },
    method: 'GET',
  });

  const visible = useBoolean(false);

  useEffect(() => {
    if (isAdmin) {
      institutionState.fetchApi();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (userProfile?.institution) {
      setInstitution(userProfile?.institution);
    }
  }, [userProfile?.institution]);

  const [step, setStep] = useState<Step>('');
  const [institution, setInstitution] = useState<Institution | null>(null);
  const [state, setState] = useState<Range[]>(defaultState);

  const handleSelectInstitution = (item: Institution | null) => {
    setStep('ExportSelection');
    setInstitution(item);
  };

  const onExportToExcelFile = async (filterDate = false) => {
    const from =
      state[0].startDate && filterDate ? startOfDay(state[0].startDate).toISOString() : undefined;
    const to =
      state[0].endDate && filterDate ? endOfDay(state[0].endDate).toISOString() : undefined;

    const result = await apiCall<any, any>({
      url: `/exports/onboarding-assessments`,
      method: 'GET',
      data: {
        'institution-id': !isAdmin ? userProfile?.institution?.id : institution?.id,
        from,
        to,
      },
      isLoading: true,
      showError: true,
      config: {
        responseType: 'blob',
      },
    });

    if (result) {
      const url = URL.createObjectURL(result);
      const fileName = `onboarding-assessments-${formatDate(new Date(), 'yyyyMMdd')}.xlsx`;

      downloadURI(url, fileName);

      setStep('');
      setInstitution(null);
      setState(defaultState);
    }
  };

  return (
    <Row direction="column" className="full-width">
      <HeaderPage
        title={'Clients'}
        hideButton={!checkRole([RoleKey.INSTITUTION_ADMIN, RoleKey.BIOLOGIC_ADMIN])}
        extraContent={
          <div>
            <Tooltip
              bg="transparent"
              trigger="click"
              placement="bottom-end"
              arrow={false}
              content={
                step === 'ListInstitution' ? (
                  <ListInstitution className="list-institution">
                    <ListInstitutionTitle>
                      Download results of baseline fitness tests
                    </ListInstitutionTitle>
                    <div style={{ flexGrow: 1, height: 0, overflow: 'auto' }}>
                      {institutionState?.state?.items?.map((item: any) => (
                        <ListInstitutionItem onClick={() => handleSelectInstitution(item)}>
                          {item.name}
                        </ListInstitutionItem>
                      ))}
                    </div>
                    <ListInstitutionItem
                      style={{ borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}
                      onClick={() => handleSelectInstitution(null)}
                    >
                      All institutions
                    </ListInstitutionItem>
                  </ListInstitution>
                ) : step === 'ExportSelection' ? (
                  <ExportSelection>
                    {isAdmin && (
                      <ListInstitutionTitle>
                        {institution?.name || 'All institutions'}
                      </ListInstitutionTitle>
                    )}
                    <ListInstitutionItem onClick={() => onExportToExcelFile()}>
                      Download Pre/Post results of all clients
                    </ListInstitutionItem>
                    <ListInstitutionItem onClick={() => setStep('DateSelection')}>
                      Download results completed in a specific time
                    </ListInstitutionItem>
                  </ExportSelection>
                ) : (
                  <DateSelection>
                    <div>
                      <DateRange
                        editableDateInputs={true}
                        moveRangeOnFirstSelection={false}
                        onChange={(item: any) => setState([item.selection])}
                        ranges={state}
                        className="export-date-range"
                      />
                      <Row
                        style={{
                          borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                          justifyContent: 'flex-end',
                          padding: 16,
                        }}
                      >
                        <CustomButton>
                          <Button placementIcon="left" onClick={() => onExportToExcelFile(true)}>
                            <DownloadIcon style={{ marginRight: 6 }} />
                            Download
                          </Button>
                        </CustomButton>
                      </Row>
                    </div>
                  </DateSelection>
                )
              }
              visible={!!step}
              allowHTML
              interactive
              onClickOutside={() => setStep('')}
            >
              <div>
                <ButtonOnboardingResults
                  variant="outlined"
                  onClick={() => setStep(isAdmin ? 'ListInstitution' : 'ExportSelection')}
                >
                  <DownloadIcon style={{ marginRight: 6 }} />
                  Onboarding results
                </ButtonOnboardingResults>
              </div>
            </Tooltip>
          </div>
        }
        extraButton={
          <TooltipStyle>
            <Tooltip
              bg="white"
              trigger="click"
              arrow={false}
              placement="bottom"
              visible={visible.value}
              offset={[0, 10]}
              allowHTML
              interactive
              content={
                <div
                  style={{
                    padding: '0 8px',
                    border: '1px solid rgba(0, 0, 0, 0.15)',
                    borderRadius: 12,
                    boxShadow:
                      '0px 12px 16px -4px rgba(0, 0, 0, 0.06), 0px 8px 12px 0px rgba(0, 0, 0, 0.06)',
                    width: 220,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 0',
                      cursor: 'pointer',
                    }}
                    onClick={goToAddPage}
                  >
                    <AddIcon />
                    <Typography
                      style={{
                        fontSize: 18,
                        lineHeight: '26px',
                        color: COLOR.surfaceNeutralBold['neutral-11'],
                      }}
                    >
                      Individual client
                    </Typography>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 0',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      visible.setValue(false);
                      openModal(MODAL_KEY.IMPORT_LEARNER);
                    }}
                  >
                    <ImportIcon />
                    <Typography
                      style={{
                        fontSize: 18,
                        lineHeight: '26px',
                        color: COLOR.surfaceNeutralBold['neutral-11'],
                      }}
                    >
                      Import clients
                    </Typography>
                  </div>
                </div>
              }
            >
              <CustomButton>
                <Button onClick={() => visible.setValue((prev) => !prev)}>
                  Add <DropdownIcon style={{ marginLeft: 6 }} />
                </Button>
              </CustomButton>
            </Tooltip>
          </TooltipStyle>
        }
      />
      <Outlet />
      <ImportModal />
    </Row>
  );
};

const ListInstitution = styled.div`
  width: 400px;
  height: 415px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: #fffdf7;
  box-shadow:
    0px 12px 16px -4px rgba(0, 0, 0, 0.06),
    0px 8px 12px 0px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ListInstitutionTitle = styled(Typography)`
  font-size: 16px;
  font-weight: 500;
  color: ${COLOR_CONFIG['neutral-text']};
  line-height: calc(22 / 16);
  padding: 16px 12px 0;
`;

const ListInstitutionItem = styled(Typography)`
  font-size: 16px;
  font-weight: 400;
  color: ${COLOR.surfaceNeutralBold['neutral-11']};
  line-height: calc(22 / 16);
  padding: 22px 12px;
  cursor: pointer;

  &:first-child {
    padding: 16px 12px 22px;
  }

  &:last-child {
    padding: 11px 12px 15px;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const ExportSelection = styled.div`
  width: 400px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background: #fffdf7;
  box-shadow:
    0px 12px 16px -4px rgba(0, 0, 0, 0.06),
    0px 8px 12px 0px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DateSelection = styled.div`
  width: 400px;

  & > div {
    border-radius: 12px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    background: #fffdf7;
    box-shadow:
      0px 12px 16px -4px rgba(0, 0, 0, 0.06),
      0px 8px 12px 0px rgba(0, 0, 0, 0.06);
    text-align: center;
    padding-top: 16px;

    & > div {
      margin: 0 auto;
    }
  }

  .export-date-range {
    .rdrDateDisplayWrapper {
      display: none;
    }

    .rdrDay {
      .rdrDayNumber {
        span {
          font-size: 16px;
          color: #222;
        }
      }
    }

    .rdrDayPassive {
      .rdrDayNumber {
        span {
          color: #808080;
        }
      }
    }

    .rdrMonthPicker,
    .rdrYearPicker {
      font-size: 16px;
    }

    .rdrWeekDay {
      font-weight: bold;
      font-size: 16px;
    }

    .rdrStartEdge,
    .rdrEndEdge {
      background: #fd8a24;

      & ~ span.rdrDayNumber span {
        color: #fffdf7 !important;
      }
    }

    .rdrInRange {
      background: #fff4e3 !important;

      & ~ span.rdrDayNumber span {
        color: #fd8a24 !important;
      }
    }

    .rdrDayToday {
      .rdrDayNumber {
        span {
          color: #fd8a24;

          &::after {
            background: #fd8a24;
          }
        }
      }
    }
  }
`;
